import {
  Box,
  Button,
  Flex,
  GridItem,
  Input,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import AceEditor from "react-ace";
import { FormProvider, useForm } from "react-hook-form";

import "ace-builds/src-noconflict/mode-sql";
import "ace-builds/src-noconflict/theme-github";
import { toast } from "react-toastify";
import apiClient from "../../../utils/ApiClient/apiClientAxios";
import CaseProcessingRules from "./CaseProcessingRules";
import CaseProHistory from "./CaseProHistory";

const CaseProcessingComp = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const methods = useForm({
    defaultValues: { name: "", rules: [] },
  });

  const onSubmitRules = async (data) => {
    console.log("===data checking", data);

    const formatRestRule = (rule) => {
      if (!rule) return rule;
      const arrayToObject = (arr) =>
        (arr || []).reduce((acc, { key, value }) => {
          if (key) acc[key] = value ?? "";
          return acc;
        }, {});

      return {
        ...rule,
        BODY: arrayToObject(rule.BODY),
        HEADERS: arrayToObject(rule.HEADERS),
        URL_PARAMS: arrayToObject(rule.URL_PARAMS),
      };
    };
    const formatExpression = (expression) => {
      if (!expression) return null;

      const { TYPE, OP, ARGS, VALUE } = expression;
      if (TYPE === "OPERATION") {
        return {
          TYPE,
          OP,
          ARGS: (ARGS || []).map(formatExpression),
        };
      }

      // LITERAL or REFERENCE → keep VALUE, remove ARGS
      if (TYPE === "LITERAL" || TYPE === "REFERENCE") {
        return {
          TYPE,
          VALUE,
        };
      }

      return { TYPE };
    };

    const formatAssignmentRule = (rule) => {
      return {
        TYPE: rule.TYPE,
        FIELD: rule.FIELD,
        EXPRESSION: formatExpression(rule.EXPRESSION),
        TEMP: rule.TEMP || false,
      };
    };

    const formatLogicalRule = (rule) => ({
      ...rule,
      TYPE: rule.TYPE,
      CONDITION: rule.CONDITION,
    });

    const formattedRules = (data.rules || []).map((rule) => {
      switch (rule.TYPE) {
        case "REST":
          return formatRestRule(rule);
        case "ASSIGNMENT":
          return formatAssignmentRule(rule);
        case "RULE":
          return formatLogicalRule(rule);
        default:
          return rule;
      }
    });

    const payload = {
      name: data.name?.trim(),
      query: query.trim(),
      rules: formattedRules,
    };

    if (!payload.query) {
      toast.warn("Please enter a query before submitting.");
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post("/caseProcessor", payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success("Case processing executed successfully.");
      setHistory((prev) => [response?.data, ...prev]);
    } catch (error) {
      console.error("Case processing error:", error);
    } finally {
      setLoading(false);
      methods.reset();
      setQuery("");
    }
  };

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await apiClient.get("/caseProcessor");
      setHistory(res.data || []);
    } catch (err) {
      console.error("Failed to load history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  return (
    <FormProvider {...methods}>
      <VStack spacing={6} align="stretch">
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize="lg" fontWeight="bold">
            Case Processing
          </Text>
          <Button
            variant="primary"
            isLoading={loading}
            onClick={methods.handleSubmit(onSubmitRules)}
          >
            Submit
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
          <GridItem colSpan={2}>
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              overflow="hidden"
            >
              <Box p={2}>
                <Text fontWeight="bold" mb={1}>
                  Name
                </Text>
                <Input
                  placeholder="Enter name"
                  {...methods.register("name", { required: false })}
                  borderRadius="md"
                  bg="gray.50"
                />
              </Box>
              <Box>
                <Text fontWeight="bold" p={2}>
                  Write your query here <br />
                  <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                    Select c.id,c.PERSON_ID,c.PRODUCT from c where c.TYPE =
                    'CASE' and
                  </span>
                </Text>
                <Box border="1px solid gray" borderRadius="sm">
                  <AceEditor
                    mode="sql"
                    theme="github"
                    value={query}
                    onChange={setQuery}
                    name="cosmos-ace-editor"
                    width="100%"
                    height="200px"
                    fontSize={14}
                    showPrintMargin={false}
                    showGutter
                    highlightActiveLine
                    setOptions={{ useWorker: false }}
                    placeholder="Select c.id,c.PERSON_ID,c.PRODUCT from c where c.TYPE = 'CASE' and"
                  />
                </Box>
              </Box>
            </Box>

            <Box my={2}>
              <CaseProcessingRules control={methods.control} name="rules" />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <CaseProHistory
              history={history}
              setHistory={setHistory}
              setQuery={setQuery}
              setValue={methods.setValue}
              loading={loadingHistory}
              fetchHistory={fetchHistory}
            />
          </GridItem>
        </SimpleGrid>
      </VStack>
    </FormProvider>
  );
};

export default CaseProcessingComp;
